import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Slider,
    Button,
    FormControlLabel,
    Switch,
    Stack,
    Paper,
    Tooltip,
    CircularProgress,
    TextField,
    InputAdornment,
    Alert,
    Tabs,
    Tab
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import SearchIcon from '@mui/icons-material/Search';

// Funkcja pomocnicza do obliczania odległości między dwoma punktami geograficznymi
const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Promień Ziemi w kilometrach
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Odległość w km
};

// Funkcja do geokodowania adresu - konwersji adresu na współrzędne geograficzne
const geocodeAddress = async (address) => {
    try {
        // Używamy Nominatim OpenStreetMap API do geokodowania
        const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}`);
        const data = await response.json();

        if (data && data.length > 0) {
            return {
                lat: parseFloat(data[0].lat),
                lng: parseFloat(data[0].lon)
            };
        }
        return null;
    } catch (error) {
        console.error('Błąd podczas geokodowania adresu:', error);
        return null;
    }
};

const LocationFilter = ({ onFilterChange }) => {
    // Dodajemy informację, że filtr działa tylko dla wydarzeń regionalnych
    const [useLocation, setUseLocation] = useState(false);
    const [radius, setRadius] = useState(100);
    const [userLocation, setUserLocation] = useState(null);
    const [locationLoading, setLocationLoading] = useState(false);
    const [locationError, setLocationError] = useState(null);

    // Nowe stany dla obsługi adresu
    const [addressInput, setAddressInput] = useState('');
    const [addressSearching, setAddressSearching] = useState(false);
    const [addressError, setAddressError] = useState(null);
    const [locationSource, setLocationSource] = useState('gps'); // 'gps' lub 'address'

    // Funkcja do pobierania lokalizacji użytkownika
    const getUserLocation = () => {
        if (!navigator.geolocation) {
            setLocationError('Geolokalizacja nie jest obsługiwana przez twoją przeglądarkę');
            return;
        }

        setLocationLoading(true);
        setLocationError(null);

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const location = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                };
                setUserLocation(location);
                setLocationLoading(false);
                setLocationSource('gps');

                // Przekaż informacje o filtrze do komponentu nadrzędnego
                onFilterChange({
                    useLocation: true,
                    radius,
                    location,
                    ranks: ['REGIONAL', 'NATIONAL', 'FOREIGN', 'INTERNATIONAL', 'LOCAL']
                });
            },
            (error) => {
                if (error) {
                    setLocationLoading(false);
                    switch (error.code) {
                        case error.PERMISSION_DENIED:
                            setLocationError('Odmówiono dostępu do lokalizacji');
                            break;
                        case error.POSITION_UNAVAILABLE:
                            setLocationError('Informacje o lokalizacji są niedostępne');
                            break;
                        case error.TIMEOUT:
                            setLocationError('Upłynął limit czasu żądania lokalizacji');
                            break;
                        default:
                            setLocationError('Wystąpił nieznany błąd podczas pobierania lokalizacji');
                            break;
                    }
                }
            }
        );
    };

    // Funkcja do wyszukiwania lokalizacji na podstawie adresu
    const searchAddressLocation = async () => {
        if (!addressInput.trim()) {
            setAddressError('Wprowadź adres');
            return;
        }

        setAddressSearching(true);
        setAddressError(null);

        try {
            const location = await geocodeAddress(addressInput);

            if (location) {
                setUserLocation(location);
                setLocationSource('address');

                // Przekaż informacje o filtrze do komponentu nadrzędnego
                onFilterChange({
                    useLocation: true,
                    radius,
                    location,
                    ranks: ['REGIONAL', 'NATIONAL', 'FOREIGN', 'INTERNATIONAL', 'LOCAL']
                });
            } else {
                setAddressError('Nie znaleziono lokalizacji dla podanego adresu');
            }
        } catch (error) {
            setAddressError('Wystąpił błąd podczas wyszukiwania adresu');
            console.error(error);
        } finally {
            setAddressSearching(false);
        }
    };

    // Obsługa zmiany promienia
    const handleRadiusChange = (event, newValue) => {
        setRadius(newValue);
        if (useLocation && userLocation) {
            onFilterChange({
                useLocation,
                radius: newValue,
                location: userLocation,
                ranks: ['REGIONAL', 'NATIONAL', 'FOREIGN', 'INTERNATIONAL', 'LOCAL']
            });
        }
    };

    // Obsługa przełącznika lokalizacji
    const handleLocationToggle = (event) => {
        const newUseLocation = event.target.checked;
        setUseLocation(newUseLocation);

        if (newUseLocation && !userLocation) {
            if (locationSource === 'gps') {
                getUserLocation();
            } else {
                searchAddressLocation();
            }
        } else {
            onFilterChange({
                useLocation: newUseLocation,
                radius,
                location: userLocation,
                ranks: newUseLocation ? ['REGIONAL', 'NATIONAL', 'FOREIGN', 'INTERNATIONAL', 'LOCAL'] : ['REGIONAL', 'NATIONAL', 'FOREIGN', 'INTERNATIONAL']
            });
        }
    };

    // Obsługa zmiany źródła lokalizacji
    const handleLocationSourceChange = (event, newValue) => {
        setLocationSource(newValue);
        setLocationError(null);
        setAddressError(null);

        // Resetowanie lokalizacji przy zmianie źródła
        if (newValue === 'gps' && useLocation) {
            getUserLocation();
        } else if (newValue === 'address' && useLocation && addressInput.trim()) {
            searchAddressLocation();
        }
    };

    // Obsługa wciśnięcia Enter w polu adresu
    const handleAddressKeyPress = (event) => {
        if (event.key === 'Enter') {
            searchAddressLocation();
        }
    };

    return (
        <Paper elevation={0} sx={{
            p: 2,
            borderRadius: '0.75rem',
            backgroundColor: 'rgba(17, 24, 39, 0.6)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgb(55, 65, 81)',
            mb: 3
        }}>
            <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: '600' }}>
                            <LocationOnIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'rgb(196, 181, 253)' }} />
                            Filtrowanie po lokalizacji
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgb(156, 163, 175)', ml: 4, display: 'block' }}>
                            Działa tylko dla wydarzeń lokalnych i regionalnych
                        </Typography>
                    </Box>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={useLocation}
                                onChange={handleLocationToggle}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                        color: 'rgb(196, 181, 253)',
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: 'rgb(109, 40, 217)',
                                    },
                                }}
                            />
                        }
                    // label={<Typography sx={{ color: 'white', fontSize: '0.875rem' }}>Aktywny</Typography>}
                    />
                </Box>

                {useLocation && (
                    <Tabs
                        value={locationSource}
                        onChange={handleLocationSourceChange}
                        sx={{
                            '& .MuiTabs-indicator': {
                                backgroundColor: 'rgb(196, 181, 253)',
                            },
                            '& .MuiTab-root': {
                                color: 'rgba(255, 255, 255, 0.7)',
                                '&.Mui-selected': {
                                    color: 'rgb(196, 181, 253)',
                                },
                            },
                        }}
                    >
                        <Tab value="gps" label="Moja lokalizacja" icon={<MyLocationIcon />} iconPosition="start" />
                        <Tab value="address" label="Wyszukaj adres" icon={<SearchIcon />} iconPosition="start" />
                    </Tabs>
                )}

                {locationLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                        <CircularProgress size={24} sx={{ color: 'rgb(196, 181, 253)' }} />
                    </Box>
                ) : useLocation && locationSource === 'gps' && !userLocation ? (
                    <Button
                        variant="outlined"
                        startIcon={<MyLocationIcon />}
                        onClick={getUserLocation}
                        sx={{
                            color: 'rgb(196, 181, 253)',
                            borderColor: 'rgb(109, 40, 217)',
                            '&:hover': {
                                borderColor: 'rgb(196, 181, 253)',
                                backgroundColor: 'rgba(109, 40, 217, 0.1)',
                            }
                        }}
                    >
                        Udostępnij lokalizację
                    </Button>
                ) : null}

                {locationError && locationSource === 'gps' && (
                    <Typography sx={{ color: 'rgb(239, 68, 68)', fontSize: '0.875rem' }}>
                        {locationError}
                    </Typography>
                )}

                {useLocation && locationSource === 'address' && (
                    <Box sx={{ mt: 1 }}>
                        <TextField
                            fullWidth
                            placeholder="Wpisz adres lub miasto"
                            value={addressInput}
                            onChange={(e) => setAddressInput(e.target.value)}
                            onKeyPress={handleAddressKeyPress}
                            disabled={addressSearching}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {addressSearching ? (
                                            <CircularProgress size={24} sx={{ color: 'rgb(196, 181, 253)' }} />
                                        ) : (
                                            <Button
                                                onClick={searchAddressLocation}
                                                sx={{
                                                    minWidth: 'auto',
                                                    color: 'rgb(196, 181, 253)',
                                                }}
                                            >
                                                <SearchIcon />
                                            </Button>
                                        )}
                                    </InputAdornment>
                                ),
                                sx: {
                                    color: 'white',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgb(75, 85, 99)',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgb(196, 181, 253)',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgb(109, 40, 217)',
                                    },
                                }
                            }}
                            sx={{
                                '& .MuiInputLabel-root': {
                                    color: 'rgba(255, 255, 255, 0.7)',
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: 'rgb(196, 181, 253)',
                                },
                            }}
                        />
                        {addressError && (
                            <Typography sx={{ color: 'rgb(239, 68, 68)', fontSize: '0.875rem', mt: 1 }}>
                                {addressError}
                            </Typography>
                        )}
                    </Box>
                )}

                {useLocation && userLocation && (
                    <>
                        <Typography sx={{ color: 'rgb(196, 181, 253)', fontSize: '0.875rem' }}>
                            Promień wyszukiwania: {radius} km
                        </Typography>
                        <Slider
                            value={radius}
                            onChange={handleRadiusChange}
                            min={10}
                            max={300}
                            step={10}
                            valueLabelDisplay="auto"
                            sx={{
                                color: 'rgb(109, 40, 217)',
                                '& .MuiSlider-thumb': {
                                    '&:hover, &.Mui-focusVisible': {
                                        boxShadow: '0 0 0 8px rgba(196, 181, 253, 0.16)',
                                    },
                                },
                                '& .MuiSlider-valueLabel': {
                                    backgroundColor: 'rgb(109, 40, 217)',
                                },
                            }}
                        />
                    </>
                )}
            </Stack>
        </Paper>
    );
};

export default LocationFilter;